import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { ProbationPolicyEditModal } from "@/pages/settings/probation-policies/widgets/ProbationPolicyEditModal.tsx";
import {
  useProbationPolicies,
  useProbationPolicyDelete,
} from "@/queries/use-probation-policies.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiProbationPolicy } from "@/types/probation-policy.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function ProbationPolicies() {
  const { t } = useTranslation();
  const { openModal } = useModals();

  useTitle(t("core:probation_policies"), t("common:settings"));

  const { data: probationPolicies, isLoading, isFetching } = useProbationPolicies();
  const probationPolicyDelete = useProbationPolicyDelete();

  const columns = useMemo<ColumnDef<ApiProbationPolicy>[]>(() => {
    return [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ];
  }, [probationPolicies]);

  const handleEditOrCreate = useCallback(
    (probationPolicy: ApiProbationPolicy | undefined = undefined) => {
      openModal(ProbationPolicyEditModal, { probationPolicy: probationPolicy });
    },
    []
  );

  const handleDelete = useCallback(async (probationPolicy: ApiProbationPolicy) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await probationPolicyDelete.mutateAsync(probationPolicy.id, {
            onSuccess() {
              analyticsService.trackEvent(analyticEvents.probationPolicies.deleted, {
                [analyticProperties.id]: probationPolicy.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.probationPolicies.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiProbationPolicy>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            void handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  return (
    <Page>
      <Page.Header
        showBack={true}
        title={t("core:probation_policies")}
        navigateTo={"/settings/policies"}
      >
        <Stack>
          <Button
            onClick={() => handleEditOrCreate()}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          >
            {t("common:create_new")}
          </Button>
        </Stack>
      </Page.Header>
      <Page.Content>
        <Table
          renderRowAction={renderRowAction}
          data={probationPolicies || []}
          columns={columns}
          isFetching={isFetching || probationPolicyDelete.isPending}
          showSkeleton={isLoading}
          notFoundComponent={<EmptyStateNothingFound />}
          emptyStateComponent={<EmptyState />}
        />
      </Page.Content>
    </Page>
  );
}
